import React, { useEffect, useRef, useState } from 'react';
import * as echarts from 'echarts';
import LoginAttempts from '../../services/loginattempt.service';
import { format, addHours } from 'date-fns';
import { useAppContext } from 'providers/AppProvider';
import { tooltipFormatterScatter } from 'helpers/echart-utils';

interface LoginAttemptsChartProps {
  selectedDate: Date;
}

const LoginAttemptsChart: React.FC<LoginAttemptsChartProps> = ({
  selectedDate
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  let myChart: echarts.ECharts | null = null;
  const [data, setData] = useState<number[][]>([]);
  const [period, setPeriod] = useState<string[]>([]);
  const factor = 10;

  const {
    config: { isDark },
    getThemeColor,
    addAlphaToHexColor
  } = useAppContext();

  useEffect(() => {
    const fetchData = async () => {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const attempts = await LoginAttempts.getLoginAttempts(dateStr);
      const newData: number[][] = attempts.map(attempt => {
        const localHour = addHours(
          new Date(selectedDate).setHours(attempt.hour),
          new Date().getTimezoneOffset() / -60
        );
        return [localHour.getHours(), attempt.count];
      });
      const newPeriod = Array.from({ length: 25 }, (_, hour) => `${hour}:00`);
      setData(newData);
      setPeriod(newPeriod);
    };

    fetchData();
  }, [selectedDate]);

  useEffect(() => {
    if (chartRef.current && data.length > 0 && period.length > 0) {
      const chartDom = chartRef.current;
      myChart = echarts.init(chartDom);

      const singleAxis: echarts.SingleAxisComponentOption[] = [
        {
          left: 20,
          type: 'category',
          boundaryGap: false,
          data: period,
          top: '4%',
          height: '86%',
          axisLabel: {
            interval: 1
          },
          axisLine: {
            lineStyle: {
              color: '#b0b0b0'
            }
          },
          splitLine: {
            show: true,
            lineStyle: {
              color: '#b8b8b8'
            }
          }
        }
      ];

      const series: echarts.ScatterSeriesOption[] = [
        {
          singleAxisIndex: 0,
          name: 'Attempts',
          coordinateSystem: 'singleAxis',
          type: 'scatter',
          data: data,
          symbolSize: function (dataItem) {
            return dataItem[1] * factor;
          },
          itemStyle: {
            color: addAlphaToHexColor(getThemeColor('primary-dark'), 0.8)
          }
        }
      ];

      const option: echarts.EChartsOption = {
        tooltip: {
          position: 'top',
          trigger: 'item',
          // eslint-disable-next-line
          backgroundColor: !isDark ? 'rgba(240, 240, 240, 0.9)' : 'rgba(49, 55, 74, 0.9)',
          textStyle: {
            color: !isDark ? '#202020' : '#ccc',
            fontFamily: 'Nunito Sans',
            fontSize: 14
          },
          formatter: tooltipFormatterScatter
        },
        toolbox: {
          itemSize: 10,
          showTitle: false,
          show: true,
          iconStyle: {
            // eslint-disable-next-line
            borderColor: !isDark? 'rgba(160, 160, 190, 0.9)': 'rgba(100, 100, 150, 0.9)'
          },
          emphasis: {
            iconStyle: { borderColor: getThemeColor('primary') }
          },
          feature: {
            mark: { show: true },
            magicType: { show: false, type: ['line', 'bar'] },
            dataZoom: { show: false },
            dataView: { show: false, readOnly: true },
            restore: { show: false },
            saveAsImage: { show: true }
          }
        },
        singleAxis: singleAxis,
        series: series
      };

      option && myChart.setOption(option);

      const resizeHandler = () => {
        if (myChart) {
          myChart.resize();
        }
      };

      window.addEventListener('resize', resizeHandler);

      return () => {
        window.removeEventListener('resize', resizeHandler);
        if (myChart) {
          myChart.dispose();
          myChart = null;
        }
      };
    }
  }, [data, period, isDark]);

  return <div ref={chartRef} style={{ width: '100%', height: '320px' }}></div>;
};

export default LoginAttemptsChart;
